import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import ProspectDialog from "@/components/ProspectDialog";
import SendEmailDialog from "@/components/SendEmailDialog";
import { useAuth } from "@/context/AuthContext";
import { canWrite } from "@/lib/roles";
import { toast } from "sonner";
import { Sprout, Plus, Mail, ArrowRightCircle, Trash2, Search } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

function RowActions({ p, onEmail, onConvert, onDelete }) {
  const btn = "inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold border transition-colors";
  return (
    <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
      <button data-testid={`prospect-email-${p.id}`} onClick={() => onEmail(p)} className={`${btn} border-[#1E73C9]/30 text-[#1E73C9] hover:bg-[#1E73C9]/10`}>
        <Mail className="h-3.5 w-3.5" /> Email
      </button>
      <button data-testid={`prospect-convert-${p.id}`} onClick={() => onConvert(p)} className={`${btn} border-[#FFCB05] bg-[#FFCB05] text-[#15233B] hover:bg-[#FBC02D]`}>
        <ArrowRightCircle className="h-3.5 w-3.5" /> Convert to lead
      </button>
      <button data-testid={`prospect-delete-${p.id}`} onClick={() => onDelete(p)} title="Delete prospect" className={`${btn} border-[#15233B]/15 text-[#15233B]/50 hover:text-[#b91c1c] hover:border-[#b91c1c]/40`}>
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default function ProspectsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const writable = canWrite(user);
  const [tab, setTab] = useState("open");
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [lookups, setLookups] = useState({ users: [], partners: [], efforts: [] });
  const [editing, setEditing] = useState(null);
  const [emailing, setEmailing] = useState(null);

  const load = useCallback(() => {
    setLoaded(false);
    api.get("/prospects", { params: { status: tab } }).then(({ data }) => setItems(data || [])).catch(() => setItems([])).finally(() => setLoaded(true));
  }, [tab]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/referral-partners", { params: { active: true } }).catch(() => ({ data: [] })),
      api.get("/marketing-efforts", { params: { active: true } }).catch(() => ({ data: [] })),
    ]).then(([u, p, m]) => setLookups({ users: u.data || [], partners: p.data || [], efforts: m.data || [] }));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((p) => [p.full_name, p.email, p.phone, p.position, p.referral_partner_name, p.county].some((v) => (v || "").toLowerCase().includes(s)));
  }, [items, q]);

  const convert = async (p) => {
    if (!window.confirm(`Convert ${p.full_name} into a Recruiting lead? They'll start at the first stage of the pipeline.`)) return;
    try {
      const { data } = await api.post(`/prospects/${p.id}/convert`);
      toast.success(`${data.full_name} is now ${data.av_id} on the Recruiting board`, { action: { label: "Open board", onClick: () => nav("/") } });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete prospect "${p.full_name}"?`)) return;
    try { await api.delete(`/prospects/${p.id}`); toast.success("Prospect deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const open = tab === "open";
  return (
    <div className="min-h-screen flex flex-col paper-bg">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl w-full mx-auto space-y-6 flex-1">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[#1E73C9]"><Sprout className="h-5 w-5" /><span className="text-[10px] uppercase tracking-[0.25em] font-bold">Recruiting · before the pipeline</span></div>
            <h1 className="text-3xl font-extrabold tracking-tight text-[#15233B]">Prospects</h1>
            <p className="text-sm text-[#15233B]/60">People you've met who haven't applied yet. No SLA timers, not counted in reports — convert them when they're ready.</p>
          </div>
          {writable && (
            <button data-testid="prospect-add-button" onClick={() => setEditing({})} className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#FFCB05] text-[#15233B] text-sm font-extrabold hover:bg-[#FBC02D] transition-colors">
              <Plus className="h-4 w-4" /> Add prospect
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {["open", "converted"].map((t) => (
            <button key={t} data-testid={`prospects-tab-${t}`} onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${tab === t ? "bg-[#15233B] text-white" : "bg-white text-[#15233B]/60 border border-[#15233B]/15 hover:text-[#15233B]"}`}>
              {t === "open" ? "Open" : "Converted"}{tab === t && loaded ? ` · ${items.length}` : ""}
            </button>
          ))}
          <div className="ml-auto relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#15233B]/40" />
            <input data-testid="prospects-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search prospects…"
              className="border border-[#15233B]/15 rounded-full pl-8 pr-3 py-1.5 text-xs bg-white w-56 focus:outline-none focus:ring-2 focus:ring-[#1E73C9]/30" />
          </div>
        </div>

        <div className="bg-white border border-[#15233B]/10 rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/55 bg-[#F6F3EC]">
                <tr>
                  <th className="text-left py-2.5 px-4">Prospect</th>
                  <th className="text-left py-2.5 px-2 hidden md:table-cell">Role interest</th>
                  <th className="text-left py-2.5 px-2 hidden md:table-cell">Source</th>
                  <th className="text-left py-2.5 px-2 hidden sm:table-cell">Owner</th>
                  <th className="text-left py-2.5 px-2">{open ? "Last emailed" : "Converted"}</th>
                  <th className="py-2.5 px-2"></th>
                </tr>
              </thead>
              <tbody data-testid="prospects-list">
                {filtered.map((p) => (
                  <tr key={p.id} data-testid={`prospect-row-${p.id}`} onClick={() => open && writable && setEditing(p)}
                    className={`border-t border-[#15233B]/8 ${open && writable ? "hover:bg-[#FFF9C4]/30 cursor-pointer" : ""}`}>
                    <td className="py-3 px-4">
                      <div className="font-bold text-[#15233B]">{p.full_name}</div>
                      <div className="text-[11px] text-[#15233B]/55">{[p.email, p.phone].filter(Boolean).join(" · ") || "No contact info"}</div>
                    </td>
                    <td className="py-3 px-2 hidden md:table-cell text-xs text-[#15233B]/70">{p.position || "—"}</td>
                    <td className="py-3 px-2 hidden md:table-cell text-xs text-[#15233B]/70">{p.referral_partner_name || p.marketing_effort_name || "—"}</td>
                    <td className="py-3 px-2 hidden sm:table-cell text-xs text-[#15233B]/70">{p.owner_name || "—"}</td>
                    <td className="py-3 px-2 text-xs text-[#15233B]/70">
                      {open
                        ? (p.last_emailed_at ? <span data-testid={`prospect-last-emailed-${p.id}`}>{fmtDate(p.last_emailed_at)} · {p.email_count}×</span> : "Never")
                        : <span className="font-bold text-[#166534]">{p.converted_av_id} · {fmtDate(p.converted_at)}</span>}
                    </td>
                    <td className="py-3 px-2">
                      {open && writable && <RowActions p={p} onEmail={setEmailing} onConvert={convert} onDelete={remove} />}
                    </td>
                  </tr>
                ))}
                {loaded && filtered.length === 0 && (
                  <tr><td colSpan={6} data-testid="prospects-empty" className="py-12 text-center">
                    <Sprout className="h-8 w-8 text-[#15233B]/20 mx-auto mb-2" />
                    <div className="text-sm font-semibold text-[#15233B]/60">{items.length === 0 ? (open ? "No prospects yet." : "No converted prospects yet.") : "No prospects match your search."}</div>
                    {open && items.length === 0 && writable && <div className="text-xs text-[#15233B]/45 mt-1">Met someone at a job fair or event? Click “Add prospect”.</div>}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <BrandFooter />
      {editing && (
        <ProspectDialog prospect={editing.id ? editing : null} {...lookups} defaultOwnerId={user?.id}
          onClose={() => setEditing(null)} onSaved={load} />
      )}
      {emailing && (
        <SendEmailDialog contact={emailing} workspace="recruiting" sendPath={`/prospects/${emailing.id}/send-email`}
          onClose={() => setEmailing(null)} onSent={load} />
      )}
    </div>
  );
}
